<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  MODE_LABELS,
  STATUS_LABELS,
  createRateCard,
  deleteRateCard,
  listRateCards,
  updateRateCard,
  type RateCard,
  type RateInput,
  type RateMode,
} from '../api/rates'

const rows = ref<RateCard[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<RateCard | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  code: '',
  name: '',
  mode: 'per_km' as RateMode,
  unit_price: 0,
  min_price: 0,
  effective_from: null as string | null,
  status: 'active' as RateCard['status'],
})

const formErrorText = computed(() => {
  if (!form.code.trim()) return '请输入规则编码'
  if (!(Number(form.unit_price) > 0)) return '单价必须大于 0'
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listRateCards({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    mode: 'per_km' as RateMode,
    unit_price: 0,
    min_price: 0,
    effective_from: null,
    status: 'active' as RateCard['status'],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: RateCard): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    mode: row.mode,
    unit_price: row.unit_price,
    min_price: row.min_price,
    effective_from: row.effective_from,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: RateInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    mode: form.mode,
    unit_price: Number(form.unit_price),
    min_price: Number(form.min_price) || 0,
    effective_from: form.effective_from || undefined,
    status: form.status,
  }
  try {
    if (editing.value) await updateRateCard(editing.value.id, input)
    else await createRateCard(input)
    formOpen.value = false
    await load(editing.value ? '计费规则已更新' : '计费规则已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: RateCard): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteRateCard(row.id)
    await load(`已删除计费规则「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-rate">
    <header class="yc-rate__bar">
      <el-input
        v-model="query.keyword"
        class="yc-rate__keyword"
        placeholder="编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-rate__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'ops:rate:create'" type="primary" @click="openCreate">新建计费规则</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-rate__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-rate__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-rate__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="编码" min-width="130" />
        <el-table-column prop="name" label="名称" min-width="170" show-overflow-tooltip />
        <el-table-column label="计费模式" width="110">
          <template #default="{ row }">{{ MODE_LABELS[row.mode as RateMode] ?? row.mode }}</template>
        </el-table-column>
        <el-table-column label="单价" width="110" align="right">
          <template #default="{ row }">{{ Number(row.unit_price).toFixed(4) }}</template>
        </el-table-column>
        <el-table-column label="起步价" width="100" align="right">
          <template #default="{ row }">{{ Number(row.min_price).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="生效日期" width="120">
          <template #default="{ row }">{{ row.effective_from || '不限' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ STATUS_LABELS[row.status as RateCard['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'ops:rate:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该计费规则？已有计费明细引用的规则不能删除。" width="280" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'ops:rate:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState v-else-if="!loading" title="还没有计费规则" description="订单签收后要按规则算出应收费用，先建一条。">
        <el-button v-perm="'ops:rate:create'" type="primary" @click="openCreate">新建计费规则</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-rate__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑计费规则 · ${editing.code}` : '新建计费规则'"
      width="520px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-rate__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="规则编码" :error="form.code.trim() ? '' : '请输入规则编码'">
          <el-input v-model="form.code" maxlength="32" placeholder="如 RC01" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 华东整车" />
        </el-form-item>

        <el-form-item label="计费模式">
          <el-select v-model="form.mode" class="yc-rate__mode">
            <el-option v-for="(label, value) in MODE_LABELS" :key="value" :label="label" :value="value" />
          </el-select>
          <span class="yc-rate__hint">订单签收后按里程 / 重量 / 重量×里程 / 单量取数</span>
        </el-form-item>

        <el-form-item label="单价" :error="Number(form.unit_price) > 0 ? '' : '单价必须大于 0'">
          <el-input-number
            v-model="form.unit_price"
            :min="0"
            :max="999999999"
            :precision="4"
            :step="0.1"
            controls-position="right"
          />
          <span class="yc-rate__hint">元 / 每计费单位</span>
        </el-form-item>

        <el-form-item label="起步价">
          <el-input-number v-model="form.min_price" :min="0" :max="999999999" :precision="2" controls-position="right" />
          <span class="yc-rate__hint">算出的金额低于起步价时按起步价收</span>
        </el-form-item>

        <el-form-item label="生效日期">
          <el-date-picker
            v-model="form.effective_from"
            type="date"
            value-format="YYYY-MM-DD"
            placeholder="不限"
            clearable
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-rate__hint yc-rate__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-rate {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__status {
    width: 140px;
  }

  &__mode {
    width: 180px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    margin-left: var(--yc-space-2);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
